/**
 * The edit decision list: Studio's entire output.
 *
 * Studio never mutates media. A project is this document — an ordered list of
 * clips, each a span of some source recording — and publishing renders a new
 * recording from it while every source stays byte-identical. That is the rule
 * that keeps invariant 1 alive (ASR ran on the exact file each source serves)
 * and it is what makes a render a cache: lose it and the manifest rebuilds it.
 *
 * Times are integer milliseconds in the **source** recording's own timeline,
 * like every other timestamp in the system. Output time is derived, never
 * stored: a clip's position in the output is the sum of the durations before
 * it, so reordering clips is reordering an array and nothing else has to move.
 */
import { z } from "zod";
import type { Ms } from "./time";

const ms = z.number().int().nonnegative();

/** Below this a clip is a flash of frames nobody meant to keep. */
export const MIN_CLIP_MS = 100;

/**
 * The three transitions, and why only three.
 *
 * Crossfade and fade-to-black cover almost everything a meeting or demo edit
 * wants; the wipe exists for the "show this, then that" demo cut. Each is one
 * ffmpeg `xfade` mode, so adding a fourth later is one enum member — the cost
 * ceiling here was never the effect, it is that every transition *overlaps*
 * two clips and therefore changes output arithmetic everywhere.
 */
export const TRANSITION_TYPES = ["crossfade", "fadeblack", "wipeleft"] as const;
export type TransitionType = (typeof TRANSITION_TYPES)[number];

export const transitionSchema = z.object({
  type: z.enum(TRANSITION_TYPES),
  /** How long the two clips overlap. Bounded so a transition stays a seam. */
  durationMs: z.number().int().min(200).max(2000).default(500),
});
export type Transition = z.infer<typeof transitionSchema>;

export const clipSchema = z
  .object({
    /** Client-generated, stable across edits so undo and drag can track it. */
    id: z.string().min(1).max(64),
    /** The source recording this span is cut from. */
    recordingId: z.string().uuid(),
    sourceStartMs: ms,
    sourceEndMs: ms,
    /**
     * Silence this clip's audio in the output. The video plays; the words are
     * not heard — so the transcript mapper drops them too, because a transcript
     * of inaudible speech is a transcript of the wrong video.
     */
    muted: z.boolean().optional(),
    /**
     * How this clip is entered from the previous one. Absent means a hard cut.
     * Stored on the incoming clip so reordering keeps the transition with the
     * clip it introduces, and the first clip's value is simply ignored.
     */
    transitionIn: transitionSchema.optional(),
    /**
     * What the person calls THIS part — "cold open", "demo take 2". Absent
     * falls back to the source's name. Per clip, not per source, because two
     * sections cut from one video are different things to the person
     * arranging them; a shared name made them indistinguishable.
     */
    label: z.string().trim().min(1).max(120).optional(),
  })
  .refine((c) => c.sourceEndMs - c.sourceStartMs >= MIN_CLIP_MS, {
    message: `A clip must be at least ${MIN_CLIP_MS}ms long.`,
  });
export type Clip = z.infer<typeof clipSchema>;

/**
 * Version 1: one video track, ordered clips, audio follows video.
 *
 * The array *is* the sequence. Multi-track (overlays, picture-in-picture) would
 * arrive as `version: 2` with an explicit track list — the version field exists
 * so a document written by that future can be recognised rather than
 * misread, and so this version can refuse it loudly.
 */
export const timelineSchema = z.object({
  version: z.literal(1),
  clips: z.array(clipSchema).max(200),
});
export type Timeline = z.infer<typeof timelineSchema>;

/** Chapters authored in Studio, in output time. Bounds repaired on publish. */
export const draftChapterSchema = z.object({
  title: z.string().trim().min(1).max(200),
  summary: z.string().max(2000).default(""),
  startMs: ms,
  endMs: ms,
});
export type DraftChapter = z.infer<typeof draftChapterSchema>;

/**
 * The overlap a transition actually gets.
 *
 * Requested duration, capped at half of the shorter neighbouring clip — an
 * overlap that swallows a whole clip is not a transition, it is a deletion the
 * user did not ask for. Zero when either neighbour is missing.
 */
type TransitionEnd = {
  sourceStartMs: Ms;
  sourceEndMs: Ms;
  transitionIn?: Transition | undefined;
};

export function effectiveTransitionMs(
  previous: TransitionEnd | undefined,
  clip: TransitionEnd | undefined,
): Ms {
  if (!previous || !clip?.transitionIn) return 0;
  const prevDur = previous.sourceEndMs - previous.sourceStartMs;
  const ownDur = clip.sourceEndMs - clip.sourceStartMs;
  return Math.min(
    clip.transitionIn.durationMs,
    Math.floor(Math.min(prevDur, ownDur) / 2),
  );
}

/**
 * Output length. Every transition overlaps two clips, so each one *shortens*
 * the output by its duration — the arithmetic that makes transitions more than
 * cosmetic, and the reason it lives here rather than in the renderer alone.
 */
export function timelineDurationMs(timeline: Timeline): Ms {
  let total = 0;
  timeline.clips.forEach((clip, i) => {
    total += clip.sourceEndMs - clip.sourceStartMs;
    total -= effectiveTransitionMs(timeline.clips[i - 1], clip);
  });
  return total;
}

/** Where each clip begins in the output, in clip order. Overlap-aware. */
export function clipOutputOffsets(timeline: Timeline): Ms[] {
  const offsets: Ms[] = [];
  let at = 0;
  timeline.clips.forEach((clip, i) => {
    at -= effectiveTransitionMs(timeline.clips[i - 1], clip);
    offsets.push(at);
    at += clip.sourceEndMs - clip.sourceStartMs;
  });
  return offsets;
}

/**
 * Is this timeline just "the whole of one recording, untouched"?
 *
 * The distinction decides what publishing means. An unedited timeline with
 * authored chapters publishes those chapters onto the *source* recording — no
 * render, no new recording, no cost. Anything else renders an output.
 */
export function isIdentityTimeline(
  timeline: Timeline,
  source: { recordingId: string; durationMs: Ms },
  toleranceMs = 500,
): boolean {
  if (timeline.clips.length !== 1) return false;
  const clip = timeline.clips[0]!;
  // A muted full-length clip is an edit — the output sounds different — so it
  // must render, not publish chapters onto the noisy original.
  if (clip.muted) return false;
  return (
    clip.recordingId === source.recordingId &&
    clip.sourceStartMs <= toleranceMs &&
    Math.abs(clip.sourceEndMs - source.durationMs) <= toleranceMs
  );
}

// ─── Transcript remapping ────────────────────────────────────────────────────

type TimedWord = { text: string; startMs: Ms; endMs: Ms };

/** A source utterance, as the mapper needs it. */
export type SourceUtterance = {
  id: string;
  clusterId: string | null;
  startMs: Ms;
  endMs: Ms;
  text: string;
  words: TimedWord[];
};

/** An utterance in the output timeline, ready to insert. */
export type MappedUtterance = {
  /** Which source recording and cluster this voice came from. */
  sourceRecordingId: string;
  sourceClusterId: string | null;
  startMs: Ms;
  endMs: Ms;
  text: string;
  words: TimedWord[];
};

const reattachPunctuation = (joined: string) =>
  joined.replace(/\s+([,.!?;:،؟])/g, "$1").trim();

/**
 * Carry transcripts through an edit, exactly.
 *
 * This is the alternative to re-transcribing on every publish, and it is not an
 * approximation. Every word already carries its own start and end from the
 * provider, and a cut is a pure time shift for everything that survives it —
 * output time is source time minus what was removed before it. So each word is
 * tested against each clip and moved by that clip's offset, and the result is
 * as aligned as the source transcript was. Nothing is interpolated, nothing is
 * guessed, and the tenant's ElevenLabs bill is untouched.
 *
 * The subtle cases, all decided by the words rather than the utterance:
 *
 * - A cut landing mid-utterance keeps exactly the words that survive, and the
 *   surviving piece takes its bounds from its own first and last word.
 * - A reorder that pulls one utterance into two different clips produces two
 *   output utterances — the words genuinely are in two places now, and one
 *   row spanning both would make click-to-seek lie.
 * - The same source span used twice (a duplicated clip) transcribes twice,
 *   because the words are genuinely heard twice.
 * - An utterance with no word timings (old data) survives only when a clip
 *   contains its midpoint, with bounds clamped to the clip — the honest best
 *   available without words.
 *
 * A word belongs to a clip when its midpoint is inside — a word straddling the
 * cut is audible in whichever half holds most of it.
 */
export function mapUtterancesThroughTimeline(
  timeline: Timeline,
  utterancesBySource: Map<string, SourceUtterance[]>,
): MappedUtterance[] {
  const offsets = clipOutputOffsets(timeline);
  const output: MappedUtterance[] = [];

  timeline.clips.forEach((clip, clipIndex) => {
    // A muted clip's words are not audible in the output, and a transcript of
    // speech nobody can hear misleads search, chat and subtitles alike.
    if (clip.muted) return;

    const sourceUtterances = utterancesBySource.get(clip.recordingId) ?? [];
    const shift = offsets[clipIndex]! - clip.sourceStartMs;

    for (const utterance of sourceUtterances) {
      // Cheap reject: no overlap at all.
      if (utterance.endMs <= clip.sourceStartMs || utterance.startMs >= clip.sourceEndMs) {
        continue;
      }

      if (utterance.words.length === 0) {
        const mid = (utterance.startMs + utterance.endMs) / 2;
        if (mid < clip.sourceStartMs || mid >= clip.sourceEndMs) continue;

        output.push({
          sourceRecordingId: clip.recordingId,
          sourceClusterId: utterance.clusterId,
          startMs: Math.max(utterance.startMs, clip.sourceStartMs) + shift,
          endMs: Math.min(utterance.endMs, clip.sourceEndMs) + shift,
          text: utterance.text,
          words: [],
        });
        continue;
      }

      const surviving = utterance.words.filter((word) => {
        const mid = (word.startMs + word.endMs) / 2;
        return mid >= clip.sourceStartMs && mid < clip.sourceEndMs;
      });

      if (surviving.length === 0) continue;

      const words = surviving.map((word) => ({
        text: word.text,
        startMs: word.startMs + shift,
        endMs: word.endMs + shift,
      }));

      output.push({
        sourceRecordingId: clip.recordingId,
        sourceClusterId: utterance.clusterId,
        startMs: words[0]!.startMs,
        endMs: words[words.length - 1]!.endMs,
        text: reattachPunctuation(surviving.map((w) => w.text).join(" ")),
        words,
      });
    }
  });

  // Output order is playback order. Within a clip the source order holds;
  // across clips the offsets decide, which is exactly what a reorder means.
  return output.sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
}
